/**
 * useEntityTabConfig
 *
 * Manages per-entity tab visibility configuration set by entity owners.
 *
 * The configuration is stored in the shared `entity-tab-config` backend plugin,
 * keyed by the entity ref — NOT in per-user storage. This guarantees that:
 *  - Owners can hide tabs from non-owners and the change is global.
 *  - Non-owners reliably stop seeing hidden tabs (they read the same record).
 *  - Every owner sees the changes made by any other owner (last write wins).
 *
 * Write access (owners only) is enforced server-side by the backend.
 *
 * The config is loaded when the entity ref becomes available (and when it changes).
 * It is not automatically refreshed on window focus; users can manually refresh the page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { entityTabConfigApiRef } from '../../../apis/EntityTabConfigClient';

export interface UseEntityTabConfigReturn {
  /** Tab titles hidden by entity owners (non-owners cannot see these). */
  ownerHiddenTabs: string[];
  /** For private entities: tabs owners intentionally reveal to non-owners. */
  ownerVisibleTabs: string[];
  /** Atomically update the hidden-tab list (owners only; enforced server-side). */
  setOwnerHiddenTabs: (tabs: string[]) => Promise<void>;
  /** Atomically update the private-entity revealed-tab list (owners only). */
  setOwnerVisibleTabs: (tabs: string[]) => Promise<void>;
  /** Re-fetch the latest config from the backend. */
  refresh: () => Promise<void>;
  /** Last server update timestamp for shared config. */
  updatedAt?: string;
  /** True when a security-finding lock blocks owner reveals on private entities. */
  securityFindingPrivateLock: boolean;
  /** Optional reason for the active security-finding lock. */
  securityFindingPrivateLockReason?: string;
  /** Last read error from the backend, if any. */
  loadError?: string;
  /** Whether config is loading. */
  loading: boolean;
}

export function useEntityTabConfig(
  entity: Entity | undefined,
): UseEntityTabConfigReturn {
  const tabConfigApi = useApi(entityTabConfigApiRef);
  const [ownerHiddenTabs, setOwnerHiddenTabsState] = useState<string[]>([]);
  const [ownerVisibleTabs, setOwnerVisibleTabsState] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);
  const [securityFindingPrivateLock, setSecurityFindingPrivateLock] =
    useState(false);
  const [securityFindingPrivateLockReason, setSecurityFindingPrivateLockReason] =
    useState<string | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  // Guard must check that entity has complete metadata to avoid incomplete refs
  const entityRef = (
    entity &&
    entity.metadata?.name &&
    entity.metadata?.namespace !== undefined
  ) ? stringifyEntityRef(entity) : undefined;

  // Guards against stale async responses when entityRef changes mid-request.
  const activeRefRef = useRef<string | undefined>(entityRef);
  activeRefRef.current = entityRef;

  const load = useCallback(
    async (ref: string | undefined) => {
      if (!ref) {
        setOwnerHiddenTabsState([]);
        setOwnerVisibleTabsState([]);
        setUpdatedAt(undefined);
        setSecurityFindingPrivateLock(false);
        setSecurityFindingPrivateLockReason(undefined);
        setLoadError(undefined);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const config = await tabConfigApi.getConfig(ref);
        if (activeRefRef.current === ref) {
          setOwnerHiddenTabsState(config.ownerHiddenTabs ?? []);
          setOwnerVisibleTabsState(config.ownerVisibleTabs ?? []);
          setUpdatedAt(config.updatedAt);
          setSecurityFindingPrivateLock(
            Boolean(config.securityFindingPrivateLock),
          );
          setSecurityFindingPrivateLockReason(
            config.securityFindingPrivateLockReason,
          );
          setLoadError(undefined);
        }
      } catch (error) {
        if (activeRefRef.current === ref) {
          setOwnerHiddenTabsState([]);
          setOwnerVisibleTabsState([]);
          setUpdatedAt(undefined);
          setSecurityFindingPrivateLock(false);
          setSecurityFindingPrivateLockReason(undefined);
          setLoadError(
            error instanceof Error
              ? error.message
              : 'Failed to load tab visibility configuration',
          );
        }
      } finally {
        if (activeRefRef.current === ref) {
          setLoading(false);
        }
      }
    },
    [tabConfigApi],
  );

  useEffect(() => {
    void load(entityRef);
  }, [load, entityRef]);

  const setOwnerHiddenTabs = useCallback(
    async (tabs: string[]) => {
      if (!entityRef) return;
      const saved = await tabConfigApi.setOwnerHiddenTabs(entityRef, tabs);
      if (activeRefRef.current === entityRef) {
        setOwnerHiddenTabsState(saved.ownerHiddenTabs ?? tabs);
        setOwnerVisibleTabsState(saved.ownerVisibleTabs ?? []);
        setUpdatedAt(saved.updatedAt);
        setSecurityFindingPrivateLock(Boolean(saved.securityFindingPrivateLock));
        setSecurityFindingPrivateLockReason(
          saved.securityFindingPrivateLockReason,
        );
        setLoadError(undefined);
      }
    },
    [tabConfigApi, entityRef],
  );

  const setOwnerVisibleTabs = useCallback(
    async (tabs: string[]) => {
      if (!entityRef) return;
      const saved = await tabConfigApi.setOwnerVisibleTabs(entityRef, tabs);
      if (activeRefRef.current === entityRef) {
        setOwnerVisibleTabsState(saved.ownerVisibleTabs ?? tabs);
        setOwnerHiddenTabsState(saved.ownerHiddenTabs ?? []);
        setUpdatedAt(saved.updatedAt);
        setSecurityFindingPrivateLock(Boolean(saved.securityFindingPrivateLock));
        setSecurityFindingPrivateLockReason(
          saved.securityFindingPrivateLockReason,
        );
        setLoadError(undefined);
      }
    },
    [tabConfigApi, entityRef],
  );

  const refresh = useCallback(() => load(entityRef), [load, entityRef]);

  return {
    ownerHiddenTabs,
    ownerVisibleTabs,
    setOwnerHiddenTabs,
    setOwnerVisibleTabs,
    refresh,
    updatedAt,
    securityFindingPrivateLock,
    securityFindingPrivateLockReason,
    loadError,
    loading,
  };
}
